import { differenceInCalendarDays } from 'date-fns';

export type ThreadDateGroup = 'today' | 'yesterday' | 'last-7-days' | 'older';

const GROUPS: ThreadDateGroup[] = ['today', 'yesterday', 'last-7-days', 'older'];

/**
 * The group of a chat last updated at `lastMessageAt`, by calendar day in local time. Last 7 days stops at six days
 * back, so with Today and Yesterday it spans one week and no weekday in it is today's. A chat with no (or an
 * unreadable) time goes under Older; one dated after `now` under Today.
 */
export const threadDateGroup = (lastMessageAt: string | null | undefined, now: Date = new Date()): ThreadDateGroup => {
    const date = lastMessageAt ? new Date(lastMessageAt) : null;
    if (!date || Number.isNaN(date.getTime())) return 'older';

    const days = differenceInCalendarDays(now, date);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    return days < 7 ? 'last-7-days' : 'older';
};

/** The chats under their date groups, in group order, each keeping the order it was given; empty groups left out. */
export const groupThreadsByDate = <T extends { lastMessageAt?: string | null }>(
    threads: readonly T[],
    now: Date = new Date()
): { group: ThreadDateGroup; threads: T[] }[] =>
    GROUPS.map((group) => ({
        group,
        threads: threads.filter((thread) => threadDateGroup(thread.lastMessageAt, now) === group),
    })).filter(({ threads: inGroup }) => inGroup.length > 0);
